import { Grid, Stack, Text, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { MultiLangCodes } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { TransitDuplicateRow } from '../TransitDuplicateRow';
import { TransitTransportEquipment } from './TransitTransportEquipment';
import { TransitViewGeneralInfoFormValues } from './validateTransitViewGeneralInfoForm';

interface TransitTransportSectionProps {
  form: UseFormReturnType<TransitViewGeneralInfoFormValues>;
  editDisabled: boolean;
  editDisabledConditionalForArrival: boolean;
  isArrival: boolean;
}

export const TransitTransportSection: React.FC<TransitTransportSectionProps> = ({ form, editDisabled, editDisabledConditionalForArrival, isArrival }) => {
  const codelistSystems = useCodeListSystem({ isTransit: true });

  return (
    <Stack mb="xl" gap="xl">
      <Stack>
        <Title order={5} fw={600}>
          Container
        </Title>
        <Grid>
          <Grid.Col span={12}>
            {editDisabledConditionalForArrival && !form.values.Consignment?.TransportEquipment?.length ? (
              <Text c="dimmed" size="sm" fs="italic">
                No container added
              </Text>
            ) : (
              <TransitTransportEquipment form={form} disabled={editDisabledConditionalForArrival} />
            )}
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack>
        <Title order={5} fw={600}>
          Transport
        </Title>
        <Grid>
          <Grid.Col span={3}>
            <MultiLangCodes
              {...form.getInputProps('Consignment.inlandModeOfTransport')}
              label="Inland mode of transport"
              system={codelistSystems}
              code="TransportModeCode"
              disabled={editDisabled}
            />
          </Grid.Col>
          {!isArrival && (
            <Grid.Col span={4}>
              <MultiLangCodes
                data-pw="mode-of-transport-at-the-border-input"
                {...form.getInputProps('Consignment.modeOfTransportAtTheBorder')}
                label="Mode of transport at the border"
                system={codelistSystems}
                code="TransportModeCode"
                disabled={editDisabled}
              />
            </Grid.Col>
          )}
        </Grid>
      </Stack>

      <Stack>
        <Title order={5} fw={600}>
          Departure transport means
        </Title>
        <TransitDuplicateRow
          addButtonLabel="Add"
          addButtonDataPW="add-departure-transport-means-btn"
          form={form}
          inputs={[
            {
              type: 'MultiLang',
              label: 'Transport ID Type',
              name: 'typeOfIdentification',
              code: 'CL750',
              width: 24,
              'data-pw': 'departure-transport-means-type-of-identification-input',
            },
            {
              type: 'Input',
              name: 'identificationNumber',
              label: 'Identification number',
              width: 24,
              'data-pw': 'departure-transport-means-identification-number-input',
            },
            {
              type: 'MultiLang',
              label: 'Nationality',
              name: 'nationality',
              width: 15,
              code: 'CL165',
              'data-pw': 'departure-transport-means-nationality-input',
            },
          ]}
          arrayPath="Consignment.DepartureTransportMeans"
          disabled={editDisabledConditionalForArrival}
        />
      </Stack>

      <Stack>
        <Title order={5} fw={600}>
          Routing
        </Title>
        <TransitDuplicateRow
          addButtonLabel="Add"
          addButtonDataPW="add-routing-btn"
          form={form}
          inputs={[
            {
              type: 'MultiLang',
              label: 'Country',
              name: 'country',
              code: 'CL008',
              width: 24,
              'data-pw': 'country-of-routing-of-consignment-country-input',
            },
          ]}
          arrayPath="Consignment.CountryOfRoutingOfConsignment"
          disabled={editDisabled}
        />
      </Stack>

      {!isArrival && (
        <Stack>
          <Stack>
            <Title order={5} fw={600}>
              Active border transport means
            </Title>
            <TransitDuplicateRow
              addButtonLabel="Add"
              addButtonDataPW="add-active-border-transport-means-btn"
              form={form}
              inputs={[
                {
                  type: 'MultiLang',
                  label: 'Transport ID Type',
                  name: 'typeOfIdentification',
                  width: 24,
                  code: 'TypeOfIdentificationofMeansOfTr',
                },
                {
                  type: 'Input',
                  name: 'identificationNumber',
                  label: 'Identification number',
                  width: 24,
                },
                {
                  type: 'MultiLang',
                  label: 'Nationality',
                  name: 'nationality',
                  width: 15,
                  code: 'CL165',
                },
                {
                  type: 'MultiLang',
                  name: 'customsOfficeAtBorderReferenceNumber',
                  label: 'Customs office at the border',
                  width: 24,
                  code: 'CL141',
                },
                {
                  type: 'Input',
                  name: 'conveyanceReferenceNumber',
                  label: 'Conveyance reference number',
                  width: 24,
                },
              ]}
              arrayPath="Consignment.ActiveBorderTransportMeans"
              disabled={editDisabled}
            />
          </Stack>

          <Stack>
            <Title order={5} fw={600}>
              Customs office of transit
            </Title>
            <TransitDuplicateRow
              addButtonLabel="Add"
              addButtonDataPW="add-customs-office-of-transit-btn"
              form={form}
              inputs={[
                {
                  type: 'MultiLang',
                  name: 'referenceNumber',
                  label: 'Customs office at the border',
                  width: 24,
                  code: 'CustomsOfficeTransit',
                },
                {
                  type: 'Input',
                  name: 'arrivalDateAndTimeEstimated',
                  label: 'Deadline',
                  width: 24,
                },
              ]}
              arrayPath="CustomsOfficeOfTransitDeclared"
              disabled={editDisabled}
            />
          </Stack>

          <Stack>
            <Title order={5} fw={600}>
              Transport charges
            </Title>
            <Grid>
              <Grid.Col span={3}>
                <MultiLangCodes
                  {...form.getInputProps('Consignment.TransportCharges.methodOfPayment')}
                  label="Method of payment"
                  system={codelistSystems}
                  code="TransportChargesMethodOfPayment"
                  disabled={editDisabled}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
